import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DataTable from 'react-data-table-component';
import {
  Badge,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Input,
  Row,
} from 'reactstrap';
import { toast, ToastContainer } from 'react-toastify';

import BreadCrumb from '../../../../Components/Common/BreadCrumb';
import ActionButton from '../../../../Components/Common/ActionButton';
import * as api from './offeringApi';
import {
  DEFAULT_PAGE_SIZE,
  OFFERING_STATUS_BADGE,
  SUBMISSION_STATUS_BADGE,
  isViewOnly,
} from './offeringConstants';

const headerLabel = label => (
  <span className='form-label text-uppercase fw-semibold fs-13 mb-0'>
    {label}
  </span>
);

const customStyles = {
  headCells: {
    style: {
      backgroundColor: '#f0f0f0',
      fontWeight: 'bold',
      fontSize: '14px',
      paddingLeft: '8px',
      paddingRight: '4px',
      borderLeft: '1px solid #dee2e6',
      whiteSpace: 'nowrap',
      alignItems: 'center',
      display: 'flex',
      overflow: 'hidden',
    },
  },
  cells: {
    style: {
      paddingLeft: '8px',
      paddingRight: '4px',
      wordBreak: 'break-word',
      borderLeft: '1px solid #dee2e6',
    },
  },
  rows: {
    style: { minHeight: '50px' },
  },
};

const OfferingListView = () => {
  const navigate = useNavigate();

  const [items, setItems] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_PAGE_SIZE);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');

  // Debounce search input
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchInput), 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Reset to page 1 when search or sort changes
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, sortField, sortDirection]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { items: rows, totalCount } = await api.listOfferings({
        page: currentPage,
        pageSize: rowsPerPage,
        searchText: debouncedSearch,
        sortField,
        sortOrder: sortDirection,
      });
      setItems(rows);
      setTotalRows(totalCount);
    } catch (err) {
      toast.error(`Gagal memuat data: ${err}`);
      setItems([]);
      setTotalRows(0);
    } finally {
      setLoading(false);
    }
  }, [currentPage, rowsPerPage, debouncedSearch, sortField, sortDirection]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSort = (column, direction) => {
    setSortField(column.sortKey || null);
    setSortDirection(direction);
  };

  const handleView = row => navigate(`/crude/offering/dtl/${row.id}`);
  const handleEdit = row => navigate(`/crude/offering/dtl/${row.id}`);

  const columns = [
    {
      name: headerLabel('Id Procurement'),
      sortKey: 'procId',
      sortable: true,
      width: '180px',
      selector: row => row.procId,
      cell: row => <span className='fw-semibold'>{row.procId}</span>,
    },
    {
      name: headerLabel('Title'),
      sortKey: 'title',
      sortable: true,
      grow: 2,
      minWidth: '220px',
      wrap: true,
      selector: row => row.title,
    },
    {
      name: headerLabel('Procurement Type'),
      sortKey: 'procurementType',
      sortable: true,
      minWidth: '200px',
      wrap: true,
      selector: row => row.procurementType,
    },
    {
      name: headerLabel('Procurement Method'),
      sortKey: 'procurementMethod',
      sortable: true,
      minWidth: '220px',
      wrap: true,
      selector: row => row.procurementMethod,
    },
    {
      name: headerLabel('Description'),
      sortKey: 'description',
      sortable: true,
      grow: 2,
      minWidth: '240px',
      wrap: true,
      selector: row => row.description,
    },
    {
      name: headerLabel('Status Offering'),
      sortKey: 'offeringStatus',
      sortable: true,
      minWidth: '160px',
      center: true,
      cell: row => (
        <Badge
          color={OFFERING_STATUS_BADGE[row.offeringStatus] || 'secondary'}
          className='px-2'
        >
          {row.offeringStatus}
        </Badge>
      ),
    },
    {
      name: headerLabel('Status Submisi'),
      sortKey: 'submissionStatus',
      sortable: true,
      minWidth: '160px',
      center: true,
      cell: row => (
        <Badge
          color={SUBMISSION_STATUS_BADGE[row.submissionStatus] || 'secondary'}
          className='px-2'
        >
          {row.submissionStatus}
        </Badge>
      ),
    },
    {
      name: headerLabel('Action'),
      sortable: false,
      width: '100px',
      center: true,
      cell: row =>
        isViewOnly(row.offeringStatus, row.submissionStatus) ? (
          <ActionButton
            variant='icon'
            size='sm'
            icon='ri-eye-line'
            label='View Detail'
            onClick={() => handleView(row)}
          />
        ) : (
          <ActionButton
            variant='icon'
            size='sm'
            icon='ri-edit-line'
            label='Edit Offer'
            onClick={() => handleEdit(row)}
          />
        ),
    },
  ];

  return (
    <div className='page-content'>
      <Container fluid>
        <BreadCrumb title='Crude Offering' pageTitle='Plan & Procurement' />
        <Row>
          <Col lg={12}>
            <Card id='OfferingList'>
              <CardHeader className='border-0 d-flex align-items-center justify-content-between flex-wrap gap-2'>
                <h5 className='card-title mb-0'>Daftar Crude Offering</h5>
                <div style={{ minWidth: 260 }}>
                  <Input
                    type='search'
                    placeholder='Cari Id Procurement, Title, Deskripsi...'
                    value={searchInput}
                    onChange={e => setSearchInput(e.target.value)}
                  />
                </div>
              </CardHeader>
              <CardBody className='pt-0'>
                <div className='table-responsive'>
                  <DataTable
                    columns={columns}
                    data={items}
                    customStyles={customStyles}
                    pagination
                    paginationServer
                    paginationTotalRows={totalRows}
                    paginationPerPage={rowsPerPage}
                    paginationDefaultPage={currentPage}
                    paginationRowsPerPageOptions={[10, 25, 50, 100]}
                    onChangePage={page => setCurrentPage(page)}
                    onChangeRowsPerPage={newPerPage => {
                      setRowsPerPage(newPerPage);
                      setCurrentPage(1);
                    }}
                    sortServer
                    onSort={handleSort}
                    wrapHeader={false}
                    highlightOnHover
                    wrap
                    dense
                    striped
                    progressPending={loading}
                    noDataComponent={
                      <div className='text-center py-4 text-muted'>
                        <i className='ri-file-list-3-line fs-24 d-block mb-2'></i>
                        Tidak ada data offering ditemukan.
                      </div>
                    }
                  />
                  <ToastContainer closeButton={false} limit={1} />
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default OfferingListView;
